
import React from "react";
import { Helmet } from "react-helmet-async";

interface PageSEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: Record<string, any>[];
  children?: React.ReactNode;
}

/**
 * Reusable SEO component that handles page metadata and structured data
 */
export function PageSEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "/lovable-uploads/f61959e9-4b01-4d53-ab30-853713f278da.png", // Hero image as default
  structuredData = [],
  children,
}: PageSEOProps) {
  // Base URL - should be determined from environment in a production app
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const fullCanonicalUrl = canonicalUrl ? (canonicalUrl.startsWith('http') ? canonicalUrl : `${baseUrl}${canonicalUrl}`) : baseUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
  const logoUrl = "/lovable-uploads/1b904982-3499-4815-a723-65da0ad254ba.png";

  // Organization schema for improved search appearance and branding
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Retail Ranker",
    "url": baseUrl,
    "logo": `${baseUrl}${logoUrl}`,
    "sameAs": [
      "https://www.facebook.com/savvyshopkeeper",
      "https://www.instagram.com/savvyshopkeeper/"
    ]
  };

  // Website schema for improved search appearance
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": baseUrl,
    "name": "Retail Ranker",
    "description": "Monitor your local rankings, competitors, and get recommendations to improve your visibility."
  };

  // Combine custom structured data with default schemas
  const allStructuredData = [organizationSchema, websiteSchema, ...structuredData];

  return (
    <Helmet>
      {/* Basic metadata */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Enhanced favicon configuration for better visibility */}
      <link rel="icon" href={logoUrl} sizes="192x192" />
      <link rel="icon" type="image/png" sizes="192x192" href={logoUrl} />
      <link rel="icon" type="image/png" sizes="128x128" href={logoUrl} />
      <link rel="icon" type="image/png" sizes="64x64" href={logoUrl} />
      <link rel="icon" type="image/png" sizes="32x32" href={logoUrl} />
      <link rel="icon" type="image/png" sizes="16x16" href={logoUrl} />
      <link rel="shortcut icon" href={logoUrl} sizes="192x192" />
      <link rel="apple-touch-icon" href={logoUrl} sizes="180x180" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph / Social */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:image" content={fullOgImage} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      {/* Structured data */}
      {allStructuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
      
      {/* Additional elements */}
      {children}
    </Helmet>
  );
}
